import * as _ from "lodash";
import { IDateRange, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import {
    WB_PeriodRow,
    WB_PlanRow,
    WB_PlanTypeEventTypeRow,
    WB_TPM_EventProductGroupRow,
    WB_TPM_EventSpendPrdGrpRow,
    WB_TPM_EventSpendRow
} from "../PA.Core/PAdbSchemaRows";
import { TPMEventBO } from "./TPMEventBO";
import { PASearch } from "../PA.Core/PASearch";
import { TPMEventBOStandardPolicy } from "./TPMEventBO.StdPolicy";
import { ErrorMessage_restate, ValidateDateRange, WarningMessage_restate } from "../PA.Core/Basic/MessageUtils";
import { TPMEventSpend } from "./TPMEventBO.ITPMEventSpend";
import { DateUtils } from "../PA.Core/Basic/Dates";
import { esWB_Period, esWB_Plan, esWB_Plan_Type_Event_Type } from "../PA.Core/PAdbSchema";

/** Type used for results from vUI vUI_TPMEvent_Prd and vUI_TPMEvent_PPG */
type overlapViewResult = {
    EVENT_ID: string;
    STATUS_TYPE: string;
    INSTORE_START_DT: string;
    INSTORE_END_DT: string;
    SHIPMENT_START_DT: string;
    SHIPMENT_END_DT: string;
    PRODUCT_ID?: string;
};

/** Type used to filter results for vUI vUI_TPMEvent_Prd and vUI_TPMEvent_PPG */
type overlapQueryStruct = {
    WB_PLAN_ID: number;
    WB_TEVT_ID: {
        neq: number;
    };
    WB_PRDGRP_ID?: {
        inq: number[];
    };
    WB_PROD_ID?: {
        inq: number[];
    };
    WB_EVTTYPE_ID: number;
    STATUS_TYPE: {
        inq: string[];
    };
    WB_CUST_ID_PLANNING?: number;
    WB_CUST_ID_SUBPLANNING?:
        | number
        | {
        inq: number[];
    };
};

export class TPMEventBOValidations {

    static async Validate(aBO: TPMEventBO): Promise<void> {

        if (!aBO.WB_TPMEvent.isActive)
            return

        await TPMEventBOValidations.ValidateEventFields(aBO)
        // Skip further validations if Errors exist
        if (aBO.ValidateMessageProvider.countMessages(MessageType.Error) > 0)
            return

        // Validate Event Spend Fields
        await TPMEventBOValidations.ValidateEventSpend(aBO)
        // Skip further validations if Errors exist
        if (aBO.ValidateMessageProvider.countMessages(MessageType.Error) > 0)
            return

        // Validate Tactic Fields
        await TPMEventBOValidations.ValidateEventPPGOverlap(aBO)
        // Skip further validations if Errors exist
        if (aBO.ValidateMessageProvider.countMessages(MessageType.Error) > 0)
            return

        // Validate Tactic Fields
        await TPMEventBOValidations.ValidateSpendAmount(aBO)
        // Skip further validations if Errors exist
        if (aBO.ValidateMessageProvider.countMessages(MessageType.Error) > 0)
            return

        // Validate Product Instore dates
        await TPMEventBOValidations.ValidateEventPrdOverlap(aBO)
        if (aBO.ValidateMessageProvider.countMessages(MessageType.Error) > 0)
            return

        TPMEventBOValidations.ValidateProductGroupCoverage(aBO)
        if (aBO.ValidateMessageProvider.countMessages(MessageType.Error) > 0)
            return

        TPMEventBOValidations.ValidateEventSpendProductsAreInEvent(aBO)
        if (aBO.ValidateMessageProvider.countMessages(MessageType.Error) > 0)
            return

    }

    static async ValidateBeforeDelete(aBO: TPMEventBO): Promise<void> {

        const anEventPolicy: TPMEventBOStandardPolicy = aBO.PolicyCollection.Event

        ErrorMessage_restate(
            aBO.DeleteMessageProvider,
            anEventPolicy && !anEventPolicy.bIsEditor,
            'UserIsntEditor',
            'User doesn\'t have Edit Access to Delete Event'
        )

        ErrorMessage_restate(
            aBO.DeleteMessageProvider,
            !aBO.PrimaryWorkflowProcessor.CurrentState.StatusTypeIsPreApproval,
            'CantDeletePostApproval',
            'Unable to delete Event that has been Approved'
        )


    }

    //  Name the functions reasonably and put them in the top Validate Function.

    static async ValidateEventFields(aBO: TPMEventBO): Promise<void> {

        const Evt = aBO.WB_TPMEvent
        const EvtType = Evt.WB_EventTypeRow
        const PlanPeriod = Evt.WB_PlanRow.WB_PeriodRow
        const PlanPeriodRange: IDateRange = { StartDt: PlanPeriod.PeriodStartDt, EndDt: PlanPeriod.PeriodEndDt }
        const dateValidateOptions = {
            bLogError: true,
            validateRange: PlanPeriodRange,
            validationRangeName: 'valid Planning Period'
        }

        //Check required fields
        let bEventNameEmpty = false
        let bEventTypeEmpty = false
        let bInitialStatusEmpty = false
        if (_.isEmpty(Evt.cif.EVENT_NAME)) {
            bEventNameEmpty = true
        }

        if (_.isUndefined(Evt.cif.WB_EVTTYPE_ID) || _.isNull(Evt.cif.WB_EVTTYPE_ID)) {
            bEventTypeEmpty = true
        }

        if (_.isUndefined(Evt.cif.WORKFLOW_STATUS_ID) || _.isNull(Evt.cif.WORKFLOW_STATUS_ID)) {
            bInitialStatusEmpty = true
        }

        // Check Dates
        if (EvtType.bInStoreDatesShowInd && EvtType.cif.INSTOREDATES_POLICY == 'UserInput') {
            ValidateDateRange(
                aBO.ValidateMessageProvider,
                'Event InStore Dates',
                Evt.InStoreStartDt,
                Evt.InStoreEndDt,
                dateValidateOptions
            )
        }

        if (EvtType.bShipmentDatesShowInd && EvtType.cif.SHIPMENTDATES_POLICY == 'UserInput') {
            ValidateDateRange(
                aBO.ValidateMessageProvider,
                'Event Shipment Dates',
                Evt.ShipmentStartDt,
                Evt.ShipmentEndDt,
                dateValidateOptions
            )
        }

        if (EvtType.bOrderDatesShowInd && EvtType.cif.ORDERDATES_POLICY == 'UserInput') {
            ValidateDateRange(
                aBO.ValidateMessageProvider,
                'Event Order Dates',
                Evt.OrderStartDt,
                Evt.OrderEndDt,
                dateValidateOptions
            )
        }

        if (EvtType.bDistributionDatesShowInd && EvtType.cif.DISTRIBUTIONDATES_POLICY == 'UserInput') {
            ValidateDateRange(
                aBO.ValidateMessageProvider,
                'Event Distribution Dates',
                Evt.DistributionStartDt,
                Evt.DistributionEndDt,
                dateValidateOptions
            )
        }

        let EvtPrdGrpsIncludeIndFlag = false
        const message: string[] = []
        const EvtPrdGrps = _.filter(
            aBO.WB_TPMEvent.WB_TPM_EventProductGroupRows,
            (x: WB_TPM_EventProductGroupRow) => x.isActive
        )
        const arrTPMEvtSpendRowsToBeValidated: WB_TPM_EventSpendRow[] = _.filter(
            aBO.WB_TPMEvent.WB_TPM_EventSpendRows,
            (aTPMEvtSRow: WB_TPM_EventSpendRow) =>
                aTPMEvtSRow.cif.PAID_IN_FULL_IND !== '1' &&
                !aTPMEvtSRow.WB_WorkflowStatusRow.StatusTypeCancelled &&
                aTPMEvtSRow.isActive &&
                aTPMEvtSRow.cif.DELETED_IND !== '1'
        )
        _.forEach(arrTPMEvtSpendRowsToBeValidated, (z: WB_TPM_EventSpendRow) => {
            const EvtSpndPrdGrp = _.filter(
                z.cif.WB_TPM_EventSpendPrdGrpRows,
                (y) => y.cif.INCLUDE_IND == '1' && y.isActive
            )
            if (EvtSpndPrdGrp.length <= 0) {
                EvtPrdGrpsIncludeIndFlag = true
                message.push(z.cif.EVENT_SPEND_NAME)
            }
        })

        let bIsBasePriceNotSet = false
        let bIsEDLPPriceNotSet = false
        let bIsPromotedPriceNotSet = false
        let bIsDiscountAmtNotSet = false
        let bIsDiscountPcntNotSet = false
        let bEDLPCoverageFactorError = false

        const isUserInput = (policyId: string): boolean => policyId.toLowerCase().includes('userinput')

        _.forEach(EvtPrdGrps, (aRow: WB_TPM_EventProductGroupRow) => {
            if (EvtType.bCoverageFactorShowInd && EvtType.cif.COVERAGEFACTOR_LEVEL == 'ProductGroup') {
                // TODO: Add Coverage Factor Policy to allow for showing field in readonly state.
                if (EvtType.cif.CALC_VOLUMELAYER_ID === 'EDLP' && aRow.COVERAGE_FACTOR_BASE_100 != 100) {
                    bEDLPCoverageFactorError = true
                }
            }

            bIsBasePriceNotSet ||=
                EvtType.bPGBasePriceShowInd &&
                EvtType.bPGBasePriceRequiredInd &&
                isUserInput(EvtType.cif.PGBASEPRICE_POLICY) &&
                (+aRow.cif.BASE_PRICE == 0)

            bIsPromotedPriceNotSet ||=
                EvtType.bPGPromotedPriceShowInd &&
                EvtType.bPGPromotedPriceRequiredInd &&
                isUserInput(EvtType.cif.PGPROMOTEDPRICE_POLICY) &&
                (+aRow.cif.PROMOTED_PRICE == 0)

            bIsEDLPPriceNotSet ||=
                EvtType.bPGEDLPPriceShowInd &&
                EvtType.bPGEDLPPriceRequiredInd &&
                isUserInput(EvtType.cif.PGEDLPPRICE_POLICY) &&
                (+aRow.cif.EDLP_PRICE == 0)

            bIsDiscountAmtNotSet ||=
                EvtType.bPGDiscountAmtShowInd &&
                EvtType.bPGDiscountAmtRequiredInd &&
                isUserInput(EvtType.cif.PGDISCOUNTAMT_POLICY) &&
                (+aRow.cif.DISCOUNT_AMT == 0)

            bIsDiscountPcntNotSet ||=
                EvtType.bPGDiscountPcntShowInd &&
                EvtType.bPGDiscountPcntRequiredInd &&
                isUserInput(EvtType.cif.PGDISCOUNTPCNT_POLICY) &&
                (+aRow.cif.DISCOUNT_PCNT == 0)

        })

        ErrorMessage_restate(
            aBO.ValidateMessageProvider,
            EvtPrdGrpsIncludeIndFlag,
            'EvtPrdGrpsIncludeIndFlag',
            `Event cannot be saved: At least one product group must be included in tactic: ${message.join(',')}`
        )
        ErrorMessage_restate(
            aBO.ValidateMessageProvider,
            bEventNameEmpty,
            'EventNameEmpty',
            'Please enter promotion name'
        )
        ErrorMessage_restate(
            aBO.ValidateMessageProvider,
            bEventTypeEmpty,
            'EventTypeEmpty',
            'Please enter promotion type'
        )
        ErrorMessage_restate(
            aBO.ValidateMessageProvider,
            bInitialStatusEmpty,
            'EventInitialStatusEmpty',
            'Please enter promotion status'
        )
        ErrorMessage_restate(
            aBO.ValidateMessageProvider,
            bEDLPCoverageFactorError,
            'EDLPCoverageFactor100',
            'Coverage Percentage should be 100'
        )
        ErrorMessage_restate(
            aBO.ValidateMessageProvider,
            bIsBasePriceNotSet,
            'BasePriceNotSet',
            'Base Price is required on this event type'
        )
        if (aBO.PrimaryWorkflowProcessor?.NextState?.StatusTypeInitial) {
            WarningMessage_restate(
                aBO.ValidateMessageProvider,
                bIsEDLPPriceNotSet,
                'EDLPPriceNotSet',
                'EDLP Price is required on this event type'
            )
            WarningMessage_restate(
                aBO.ValidateMessageProvider,
                bIsPromotedPriceNotSet,
                'PromotedPriceNotSet',
                'Promo Price is required on this event type'
            )
        }
        else{
            ErrorMessage_restate(
                aBO.ValidateMessageProvider,
                bIsEDLPPriceNotSet,
                'EDLPPriceNotSet',
                'EDLP Price is required on this event type'
            )
            ErrorMessage_restate(
                aBO.ValidateMessageProvider,
                bIsPromotedPriceNotSet,
                'PromotedPriceNotSet',
                'Promo Price is required on this event type'
            )
        }

        ErrorMessage_restate(
            aBO.ValidateMessageProvider,
            bIsDiscountAmtNotSet,
            'DiscountAmtNotSet',
            'Discount amount is required on this event type'
        )
        ErrorMessage_restate(
            aBO.ValidateMessageProvider,
            bIsDiscountPcntNotSet,
            'DiscountPcntNotSet',
            'Discount percent is required on this event type'
        )

    }

    static async ValidateSpendAmount(aBO: TPMEventBO): Promise<void> {
        let isSpendError = false

        const EvtSpends = _.filter(aBO.WB_TPMEvent.WB_TPM_EventSpendRows, (x) => x.isActive)
        // let EvtSpendsRows: any = _.filter(aBO.WB_TPMEvent.WB_TPM_EventSpendRows, (x: ITPMEventSpend) => x.isActive)
        let greaterThanOneItems = ''

        //WB_TPM_EventSpendPrdGrpRows
        _.forEach(EvtSpends, (aRow) => {
            _.forEach(aRow.WB_TPM_EventSpendPrdGrpRows, (bRow) => {
                if (bRow.isActive && aRow.cif.RATE_UOM === 'ListSales' && bRow.cif.RATE_AMT > 1) {
                    isSpendError = true
                    if (greaterThanOneItems) {
                        greaterThanOneItems =
                            greaterThanOneItems + ',' + bRow.WB_ProductGroupRow.cif.PRODUCT_GROUP_NAME
                    } else {
                        greaterThanOneItems =
                            greaterThanOneItems + bRow.WB_ProductGroupRow.cif.PRODUCT_GROUP_NAME
                    }
                }
            })
        })
        if (isSpendError) {
            aBO.ValidateMessageProvider.addMessage({
                code: 'ValidAmount',
                type: MessageType.Error,
                title:
                    'Percent-based discount rate entered is greater than 1 for ' +
                    greaterThanOneItems +
                    '. Users should enter rates as decimals (e.g. 10% should be entered as 0.10)',
                description: '',
                subtitle: '',
                count: 0
            })
        } else {
            aBO.ValidateMessageProvider.removeMessage('ValidAmount')
        }
    }

    static ValidateEventSpend(aBO: TPMEventBO): Promise<void> {
        return new Promise((resolve, error) => {
            async function RunEventSpendValidateFunctions() {
                //
                for (const iEventSpend of aBO.TPMEventSpends) {
                    const oEventSpend: TPMEventSpend = iEventSpend;
                    await oEventSpend.Validate({});
                    aBO.ValidateMessageProvider.addMessages(oEventSpend.ValidateMessageProvider.Messages);
                }
            }

            RunEventSpendValidateFunctions()
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static async ValidateEventPPGOverlap(aBO: TPMEventBO): Promise<void> {
        const Evt = aBO.WB_TPMEvent;

        // Every early-exit below means "overlap no longer applies" - clear any overlap message a
        // previous save may have left behind so it doesn't linger once the condition is resolved.
        const clearOverlapMessages = () => {
            aBO.ValidateMessageProvider.removeMessage("ShipmentDateOverlap");
            aBO.ValidateMessageProvider.removeMessage("InStoreDateOverlap");
        };

        // Don't check overlap when Cancelling an Event
        if (aBO.PrimaryWorkflowProcessor.NextState.StatusTypeCancelled) {
            clearOverlapMessages();
            return;
        }

        const ignoreInStorePPGOverlapPolicy: boolean = Evt.WB_EventTypeRow.cif.INSTORE_PGOVERLAP_POLICY === "None";
        const ignoreShipmentPPGOverlapPolicy: boolean = Evt.WB_EventTypeRow.cif.SHIPMENT_PGOVERLAP_POLICY === "None";

        if (ignoreInStorePPGOverlapPolicy && ignoreShipmentPPGOverlapPolicy) {
            clearOverlapMessages();
            return;
        }

        const vUI_TPMEvent_PPG_columns = {
            WB_PLAN_ID: { type: "number" },
            WB_TEVT_ID: { type: "number" },
            WB_EVTTYPE_ID: { type: "number" },
            WB_CUST_ID_SUBPLANNING: { type: "number" },
            WB_PRDGRP_ID: { type: "number" },
            INSTORE_START_DT: { type: "string" },
            INSTORE_END_DT: { type: "string" },
            SHIPMENT_START_DT: { type: "string" },
            SHIPMENT_END_DT: { type: "string" },
            STATUS_TYPE: { type: "string" }
        };

        const bLogAsError =
            aBO.PrimaryWorkflowProcessor.CurrentState.StatusTypeIsPostApproval ||
            aBO.PrimaryWorkflowProcessor.CurrentState.StatusTypeIsRequestingApproval ||
            aBO.PrimaryWorkflowProcessor.NextState.StatusTypeIsRequestingApproval ||
            aBO.PrimaryWorkflowProcessor.NextState.StatusTypeApproved;

        // only check for overlaps when the other event's status will produce a warning or an error on overlap
        const findStatusTypes = [
            ...aBO.WB_TPMEvent.WB_EventTypeRow.OverlapErrorStatuses,
            ...aBO.WB_TPMEvent.WB_EventTypeRow.OverlapWarningStatuses
        ];

        if (findStatusTypes.length === 0) {
            clearOverlapMessages();
            return;
        }

        if (aBO.ProductGroups.length === 0) {
            clearOverlapMessages();
            return;
        }

        const arrPrdGrps: number[] = aBO.ProductGroups.map((item) => item.cif.WB_PRDGRP_ID);

        const query: overlapQueryStruct = {
            WB_PLAN_ID: Evt.cif.WB_PLAN_ID,
            WB_TEVT_ID: { neq: Evt.cif.WB_TEVT_ID },
            WB_PRDGRP_ID: { inq: arrPrdGrps },
            WB_EVTTYPE_ID: Evt.cif.WB_EVTTYPE_ID,
            STATUS_TYPE: { inq: findStatusTypes }
        };
        if (Evt.cif.WB_CUST_ID_SUBPLANNING > 0) {
            query.WB_CUST_ID_SUBPLANNING = { inq: [Evt.cif.WB_CUST_ID_SUBPLANNING, 0] };
        }

        const inStorePredicate = {
            INSTORE_START_DT: { lt: Evt.cif.INSTORE_END_DT },
            INSTORE_END_DT: { gt: Evt.cif.INSTORE_START_DT }
        };
        const shipmentPredicate = {
            SHIPMENT_START_DT: { lt: Evt.cif.SHIPMENT_END_DT },
            SHIPMENT_END_DT: { gt: Evt.cif.SHIPMENT_START_DT }
        };

        // Issue separate fully-scoped searches per date basis rather than OR-ing the two date
        // predicates into one query: the search builder does not parenthesize a top-level "or"
        // against sibling scope conditions, so a combined query silently drops the scope filters
        // (WB_PLAN_ID, event type, PPG, status, sub-customer) from the shipment branch.
        const [inStoreRows, shipmentRows] = await Promise.all([
            PASearch.find("vUI_TPMEvent_PPG", { ...query, ...inStorePredicate }, vUI_TPMEvent_PPG_columns, [], 0, []),
            PASearch.find("vUI_TPMEvent_PPG", { ...query, ...shipmentPredicate }, vUI_TPMEvent_PPG_columns, [], 0, [])
        ]);
        const arrRows = <overlapViewResult[]>[...inStoreRows, ...shipmentRows];

        const getOverlapType = (row: overlapViewResult): "ship" | "instore" | "both" | "none" => {
            const inStoreStart = DateUtils.convertFromSAPBW(row.INSTORE_START_DT);
            const inStoreEnd = DateUtils.convertFromSAPBW(row.INSTORE_END_DT);
            const shipStart = DateUtils.convertFromSAPBW(row.SHIPMENT_START_DT);
            const shipEnd = DateUtils.convertFromSAPBW(row.SHIPMENT_END_DT);

            const overlapsInstore = DateUtils.RangeDatesOverlap(
                inStoreStart,
                inStoreEnd,
                aBO.WB_TPMEvent.InStoreStartDt,
                aBO.WB_TPMEvent.InStoreEndDt
            );
            const overlapsShipment = DateUtils.RangeDatesOverlap(
                shipStart,
                shipEnd,
                aBO.WB_TPMEvent.ShipmentStartDt,
                aBO.WB_TPMEvent.ShipmentEndDt
            );

            switch (true) {
                case overlapsShipment &&
                overlapsInstore &&
                !ignoreInStorePPGOverlapPolicy &&
                !ignoreShipmentPPGOverlapPolicy:
                    return "both";
                case overlapsShipment && !ignoreShipmentPPGOverlapPolicy:
                    return "ship";
                case overlapsInstore && !ignoreInStorePPGOverlapPolicy:
                    return "instore";
                default:
                    return "none";
            }
        };

        const shipOverlapErrors: string[] = [];
        const shipOverlapWarnings: string[] = [];

        const inStoreOverlapErrors: string[] = [];
        const inStoreOverlapWarnings: string[] = [];
        arrRows.forEach((item) => {
            const overlapType = getOverlapType(item);
            if (overlapType === "none") {
                return;
            }
            const overlapIsError = aBO.WB_TPMEvent.WB_EventTypeRow.hasOverlapErrorForStatus(item.STATUS_TYPE);
            const overlapIsWarning = aBO.WB_TPMEvent.WB_EventTypeRow.hasOverlapWarningForStatus(item.STATUS_TYPE);

            if (bLogAsError && overlapIsError) {
                // log as an error if the status type is configured to have an error on overlap, and the source event's status is sufficient to generate an error on overlap
                switch (overlapType) {
                    case "ship":
                        shipOverlapErrors.push(item.EVENT_ID);
                        break;
                    case "instore":
                        inStoreOverlapErrors.push(item.EVENT_ID);
                        break;
                    case "both":
                        inStoreOverlapErrors.push(item.EVENT_ID);
                        shipOverlapErrors.push(item.EVENT_ID);
                }
                return;
            }

            // if this event isn't generating errors on overlaps because of its status, all overlaps with configured status types will be warnings
            if (overlapIsWarning || overlapIsError) {
                switch (overlapType) {
                    case "ship":
                        shipOverlapWarnings.push(item.EVENT_ID);
                        break;
                    case "instore":
                        inStoreOverlapWarnings.push(item.EVENT_ID);
                        break;
                    case "both":
                        inStoreOverlapWarnings.push(item.EVENT_ID);
                        shipOverlapWarnings.push(item.EVENT_ID);
                }
            }
        });

        // The view returns one row per PPG, so an event with multiple overlapping PPGs pushes its
        // EVENT_ID more than once - de-duplicate before composing the message text. Error and
        // Warning share one message code per date basis (both can be present at once, for
        // different overlapping events), so clear the code first and then re-add whichever
        // severities currently apply - otherwise a message from a prior save with a different
        // severity mix would never fully go away once the overlap is resolved.
        const restateOverlapMessage = (errorIds: string[], warningIds: string[], code: string, describe: (ids: string) => string) => {
            const uniqueErrorIds = _.uniq(errorIds);
            const uniqueWarningIds = _.uniq(warningIds);
            aBO.ValidateMessageProvider.removeMessage(code);
            if (uniqueErrorIds.length > 0) {
                ErrorMessage_restate(aBO.ValidateMessageProvider, true, code, describe(uniqueErrorIds.join(", ")));
            }
            if (uniqueWarningIds.length > 0) {
                WarningMessage_restate(aBO.ValidateMessageProvider, true, code, describe(uniqueWarningIds.join(", ")));
            }
        };

        restateOverlapMessage(
            shipOverlapErrors,
            shipOverlapWarnings,
            "ShipmentDateOverlap",
            (ids) =>
                "This promotion overlaps with " +
                ids +
                " having the same customer and/or sub-customer and shipment dates, as well as at least one of the same PPGs"
        );
        restateOverlapMessage(
            inStoreOverlapErrors,
            inStoreOverlapWarnings,
            "InStoreDateOverlap",
            (ids) =>
                "This promotion overlaps with " +
                ids +
                " having the same customer and/or sub-customer and performance dates, as well as at least one of the same PPGs"
        );
    }

    static async ValidateEventPrdOverlap(aBO: TPMEventBO): Promise<void> {
        const Evt = aBO.WB_TPMEvent;

        // Uses its own code (distinct from ValidateEventPPGOverlap's "ShipmentDateOverlap") so the
        // two validations can restate/clear their messages independently without clobbering each
        // other - removeMessage matches by code alone, regardless of Error/Warning type.
        const prdOverlapMessageCode = "ShipmentDateOverlapPrd";

        // Every early-exit below means "overlap no longer applies" - clear any message a previous
        // save may have left behind so it doesn't linger once the condition is resolved.
        const clearOverlapMessage = () => aBO.ValidateMessageProvider.removeMessage(prdOverlapMessageCode);

        const ignoreShipmentPrdOverlapPolicy: boolean = Evt.WB_EventTypeRow.cif.SHIPMENT_PRDOVERLAP_POLICY == "None";
        if (ignoreShipmentPrdOverlapPolicy) {
            clearOverlapMessage();
            return;
        }

        const bLegacyPlan: boolean = Evt.WB_PlanRow.WB_PlanTypeRow.cif.DISPLAY_HANDLING_METHOD_ID == "Legacy";
        if (bLegacyPlan) {
            clearOverlapMessage();
            return;
        }

        const vUI_TPMEvent_Prd_columns = {
            WB_PLAN_ID: { type: "number" },
            WB_TEVT_ID: { type: "number" },
            WB_EVTTYPE_ID: { type: "number" },
            WB_CUST_ID_SUBPLANNING: { type: "number" },
            WB_PROD_ID: { type: "number" },
            INSTORE_START_DT: { type: "string" },
            INSTORE_END_DT: { type: "string" },
            SHIPMENT_START_DT: { type: "string" },
            SHIPMENT_END_DT: { type: "string" },
            STATUS_TYPE: { type: "string" }
        };

        // Don't check overlap when Cancelling an Event
        if (aBO.PrimaryWorkflowProcessor.NextState.StatusTypeCancelled) {
            clearOverlapMessage();
            return;
        }

        // only check for overlaps when the other event's status will produce a warning or an error on overlap
        const findStatusTypes = [
            ...Evt.WB_EventTypeRow.OverlapErrorStatuses,
            ...Evt.WB_EventTypeRow.OverlapWarningStatuses
        ];

        if (findStatusTypes.length === 0) {
            clearOverlapMessage();
            return;
        }

        const prdIds = aBO.WB_TPMEvent.WB_TPM_EventProductRows.filter(
            (prdRecord) => prdRecord.cif.ROW_TYPE === "Display" && prdRecord.isActive
        ).map((prdRecord) => prdRecord.cif.WB_PROD_ID);

        if (prdIds.length === 0) {
            clearOverlapMessage();
            return;
        }

        const queryShipment: overlapQueryStruct = {
            WB_PLAN_ID: Evt.cif.WB_PLAN_ID,
            WB_TEVT_ID: { neq: Evt.cif.WB_TEVT_ID },
            WB_PROD_ID: { inq: prdIds },
            WB_EVTTYPE_ID: Evt.cif.WB_EVTTYPE_ID,
            STATUS_TYPE: { inq: findStatusTypes }
        };

        if (Evt.WB_EventTypeRow.cif.SHIPMENT_PRDOVERLAP_POLICY == "ByEventTypeAndCustomer") {
            if (Evt.cif.WB_CUST_ID_SUBPLANNING > 0)
                queryShipment.WB_CUST_ID_SUBPLANNING = Evt.cif.WB_CUST_ID_SUBPLANNING;
            queryShipment.WB_CUST_ID_PLANNING = Evt.cif.WB_CUST_ID_PLANNING;
        }

        const shipmentPredicate = {
            SHIPMENT_END_DT: { gt: Evt.cif.SHIPMENT_START_DT },
            SHIPMENT_START_DT: { lt: Evt.cif.SHIPMENT_END_DT }
        };

        const arrRows = <overlapViewResult[]>(
            await PASearch.find(
                "vUI_TPMEvent_Prd",
                { ...queryShipment, ...shipmentPredicate },
                vUI_TPMEvent_Prd_columns,
                [],
                0,
                []
            )
        );
        if (arrRows.length === 0) {
            clearOverlapMessage();
            return;
        }

        const bLogAsError =
            aBO.PrimaryWorkflowProcessor.CurrentState.StatusTypeIsPostApproval ||
            aBO.PrimaryWorkflowProcessor.CurrentState.StatusTypeIsRequestingApproval ||
            aBO.PrimaryWorkflowProcessor.NextState.StatusTypeIsRequestingApproval ||
            aBO.PrimaryWorkflowProcessor.NextState.StatusTypeApproved;

        const errorEvtIds: string[] = [];
        const errorPrdIds: string[] = [];
        const warningEvtIds: string[] = [];
        const warningPrdIds: string[] = [];
        arrRows.forEach((item) => {
            // if the event being validated is not in a state to produce an error, then even error statuses should be logged as warnings
            if (Evt.WB_EventTypeRow.hasOverlapErrorForStatus(item.STATUS_TYPE) && bLogAsError) {
                errorEvtIds.push(item.EVENT_ID);
                errorPrdIds.push(item.PRODUCT_ID);
                return;
            }
            warningEvtIds.push(item.EVENT_ID);
            warningPrdIds.push(item.PRODUCT_ID);
        });

        // Clear the code first, then re-add whichever severities currently apply - otherwise a
        // message from a prior save would never go away once the overlap is resolved.
        aBO.ValidateMessageProvider.removeMessage(prdOverlapMessageCode);
        if (errorEvtIds.length > 0) {
            ErrorMessage_restate(
                aBO.ValidateMessageProvider,
                true,
                prdOverlapMessageCode,
                `${errorPrdIds.join(", ")} product(s) within the event overlap with ${errorEvtIds.join(", ")} event. Please remove this product or change the planning dates so they do not overlap with other shipper events`
            );
        }
        if (warningEvtIds.length > 0) {
            WarningMessage_restate(
                aBO.ValidateMessageProvider,
                true,
                prdOverlapMessageCode,
                `${warningPrdIds.join(", ")} product(s) within the event overlap with ${warningEvtIds.join(", ")} event. Please remove this product or change the planning dates so they do not overlap with other shipper events`
            );
        }
    }

    static async ValidateEventCopy(aPlanId: number, oldBO: TPMEventBO): Promise<void> {
        const plan: WB_PlanRow = await oldBO.dbContext.find(esWB_Plan, aPlanId);
        const planTypeId: number = plan.cif.WB_PLANM_ID;

        const planTypeEventTypeRows: WB_PlanTypeEventTypeRow[] = await oldBO.dbContext.findAll(
            esWB_Plan_Type_Event_Type,
            {
                where: {
                    WB_PLANM_ID: planTypeId
                }
            }
        );

        const periodRow: WB_PeriodRow = await oldBO.dbContext.find(esWB_Period, plan.cif.WB_PERIOD_ID);
        const planPeriodStartDt = periodRow.PeriodStartDt;

        const evtTypeId = oldBO.WB_TPMEvent.cif.WB_EVTTYPE_ID;
        const planTypeEventTypeRow: WB_PlanTypeEventTypeRow = _.first(
            _.filter(planTypeEventTypeRows, (aRow: WB_PlanTypeEventTypeRow) => {
                return aRow.cif.WB_EVTTYPE_ID == evtTypeId;
            })
        );
        let bValidCopyEvent: boolean = false;

        if (!_.isUndefined(planTypeEventTypeRow)) {
            const planTypeEventTypeEffectiveEndDt: Date = planTypeEventTypeRow.EffectiveEndDt;
            if (DateUtils.isGreaterThanEqual(planTypeEventTypeEffectiveEndDt, planPeriodStartDt))
                bValidCopyEvent = true;
        }

        if (!bValidCopyEvent) {
            ErrorMessage_restate(
                oldBO.ValidateMessageProvider,
                true,
                "ValidCopyEvent",
                `TPM Event cannot be created because the selected event type: ` +
                oldBO.WB_TPMEvent.WB_EventTypeRow.cif.TYPE_NAME +
                ` is not valid for the selected plan: ` +
                plan.cif.PLAN_NAME
            );
        }
    }

    static ValidateProductGroupCoverage(aBO: TPMEventBO): void {
        const Evt = aBO.WB_TPMEvent;
        if (
            Evt.WB_EventTypeRow.cif.COVERAGEFACTOR_WARNING_SHOW_IND == "1" &&
            Evt.WB_EventTypeRow.cif.COVERAGEFACTOR_SHOW_IND == "1"
        ) {
            if (_.filter(Evt.WB_TPM_EventProductGroupRows, (aRow) => aRow.cif.COVERAGE_FACTOR >= 1).length) {
                WarningMessage_restate(aBO.ValidateMessageProvider, true, "CoveragePct", {
                    Title: "100% Coverage Alert",
                    Desc: `Coverage % for this event is 100% and above, please review to confirm that is correct. `
                });
            }
        }
    }

    static ValidateEventSpendProductsAreInEvent(aBO: TPMEventBO): void {
        type evtsPGSet = {
            evtSRow: WB_TPM_EventSpendRow;
            PGs: WB_TPM_EventSpendPrdGrpRow[];
        };

        let dctEvtPGs = _.keyBy(_.filter(aBO.WB_TPMEvent.WB_TPM_EventProductGroupRows,
            (a) => a.isActive && a.WB_ProductGroupRow),
            (b:WB_TPM_EventProductGroupRow) => b.cif.WB_PRDGRP_ID)

        const eventSpendsWithExtraPGs: evtsPGSet[] = [];
        for (const evtS of aBO.WB_TPMEvent.WB_TPM_EventSpendRows) {
            if(!evtS.isActive)
                continue

            const extraPGs = evtS.WB_TPM_EventSpendPrdGrpRows.filter(
                (item) => item.isActive && !(item.cif.WB_PRDGRP_ID in dctEvtPGs),
            );
            if (extraPGs.length === 0) {
                continue;
            }

            eventSpendsWithExtraPGs.push({
                evtSRow: evtS,
                PGs: extraPGs
            });
        }

        if (eventSpendsWithExtraPGs.length === 0) {
            return;
        }

        for (const evtSPgs of eventSpendsWithExtraPGs) {
            const pgString = evtSPgs.PGs.map((item) => item.WB_ProductGroupRow.cif.PRODUCT_GROUP_ID).join(", ");
            ErrorMessage_restate(
                aBO.ValidateMessageProvider,
                true,
                "EventSpendPGsNotInEvent",
                `Event spend ${evtSPgs.evtSRow.cif.EVENT_SPEND_ID} includes products not included in the event. Add the products to the event or remove them from the event spend:\n${pgString}`
            );
        }
    }
}
